import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { PriceListKind, PriceListRequest, PriceListResponse } from '../../model/priceListTypes';

const CODE_PATTERN = /^[A-Za-z0-9-]+$/;

export interface PriceListFormDialogProps {
  open: boolean;
  /** Present = edit (code locked); absent = create. */
  priceList?: PriceListResponse;
  pending?: boolean;
  onClose: () => void;
  onSave: (body: PriceListRequest) => void;
}

/** Create / edit price-list dialog (design spec §8.2) — code is immutable after creation, the
 * channel-stock groups-panel precedent; ADJUSTMENT is visible but disabled until Phase 5. */
export function PriceListFormDialog({
  open,
  priceList,
  pending = false,
  onClose,
  onSave,
}: PriceListFormDialogProps) {
  const { t } = useTranslation('catalog');
  const isEdit = Boolean(priceList);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [kind] = useState<PriceListKind>('STATIC'); // ADJUSTMENT is disabled-only in v1 — see the RadioGroup below.

  useEffect(() => {
    if (!open) return;
    setName(priceList?.name ?? '');
    setCode(priceList?.code ?? '');
  }, [open, priceList]);

  const nameValid = name.trim() !== '';
  const codeValid = isEdit || (code.trim() !== '' && CODE_PATTERN.test(code.trim()));
  const valid = nameValid && codeValid;

  const handleSave = () => {
    onSave({
      name: name.trim(),
      ...(isEdit ? {} : { code: code.trim() }),
      kind,
    });
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('priceListsPanel.editTitle') : t('priceListsPanel.create')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('priceListsPanel.nameLabel')}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={name !== '' && !nameValid}
            fullWidth
            autoFocus
          />
          <Tooltip title={isEdit ? t('priceListsPanel.codeImmutable') : ''} disableHoverListener={!isEdit}>
            <span>
              <TextField
                label={t('priceListsPanel.codeLabel')}
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                error={!isEdit && code !== '' && !codeValid}
                helperText={isEdit ? t('priceListsPanel.codeImmutable') : t('priceListsPanel.codeHelper')}
                disabled={isEdit}
                fullWidth
                slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
              />
            </span>
          </Tooltip>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('priceListsPanel.kindLabel')}
            </Typography>
            <RadioGroup value={kind}>
              <FormControlLabel
                value="STATIC"
                control={<Radio />}
                label={
                  <Stack spacing={0}>
                    <Typography>{t('priceListsPanel.kind.static')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('priceListsPanel.kindStaticHint')}
                    </Typography>
                  </Stack>
                }
              />
              <Tooltip title={t('priceListsPanel.kindAdjustmentLaterTooltip')}>
                <span>
                  <FormControlLabel
                    value="ADJUSTMENT"
                    disabled
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Stack spacing={0}>
                          <Typography color="text.disabled">
                            {t('priceListsPanel.kind.adjustment')}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {t('priceListsPanel.kindAdjustmentHint')}
                          </Typography>
                        </Stack>
                        <Chip size="small" label={t('priceListsPanel.kindAdjustmentLater')} />
                      </Stack>
                    }
                  />
                </span>
              </Tooltip>
            </RadioGroup>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleSave}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
